'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import type { DocumentoPadrao, DocumentoPadraoUso, DocumentoPadraoUsoOrigem, DocumentosPadraoSetor } from '@/features/documentosPadrao/types/documentosPadraoTypes';
import { DOCUMENTO_PADRAO_TIPO_LABEL, rotuloEscopo, rotuloProdutos, situacaoValidade } from '@/features/documentosPadrao/utils/documentosPadraoRegras';
import { Badge } from '@/shared/components/ui/common/Badge';
import { Button } from '@/shared/components/ui/common/Button';
import { Drawer } from '@/shared/components/ui/common/Drawer';
import { IconButton } from '@/shared/components/ui/common/IconButton';
import { chaveDoDiaEmSaoPaulo, formatDataPrazo, formatDateEuropean, formatFileSize } from '@/shared/formatters';
import { FeatherArchive, FeatherDownload, FeatherEdit3, FeatherEye, FeatherFileText, FeatherHandshake, FeatherInfo, FeatherMessageSquare, FeatherRotateCcw, FeatherScale, FeatherSend, FeatherTrash2, FeatherUpload, FeatherX } from '@subframe/core';
import Link from 'next/link';
const USOS_VISIVEIS_INICIALMENTE = 2;
const ORIGEM_LABEL: Record<DocumentoPadraoUsoOrigem, string> = {
  PROCESSO_ARBITRAL: 'Processo arbitral',
  NOTIFICACAO: 'Notificação extrajudicial',
  NEGOCIACAO: 'Negociação',
  MEDIACAO: 'Mediação'
};
const iconeOrigem = (origem: DocumentoPadraoUsoOrigem) => {
  if (origem === 'PROCESSO_ARBITRAL') return <FeatherScale className="h-[18px] w-[18px]" />;
  if (origem === 'NOTIFICACAO') return <FeatherSend className="h-[18px] w-[18px]" />;
  if (origem === 'NEGOCIACAO') return <FeatherHandshake className="h-[18px] w-[18px]" />;
  return <FeatherMessageSquare className="h-[18px] w-[18px]" />;
};
const rotuloUso = (uso: DocumentoPadraoUso, referencia: string): string => {
  const mesmoDia = chaveDoDiaEmSaoPaulo(uso.usadoEm) === chaveDoDiaEmSaoPaulo(referencia);
  return `${ORIGEM_LABEL[uso.origem]} · ${mesmoDia ? 'anexado hoje' : `anexado em ${formatDateEuropean(uso.usadoEm)}`}`;
};
interface DocumentoPadraoDetalhesDrawerProps {
  aberto: boolean;
  documento: DocumentoPadrao | null;
  setores: DocumentosPadraoSetor[];
  referencia: string;
  diasAviso?: number;
  onFechar: () => void;
  onEditar: (documento: DocumentoPadrao) => void;
  onSubstituirArquivo: (documento: DocumentoPadrao) => void;
  onRestaurarVersao: (documento: DocumentoPadrao, versao: number) => void;
  onArquivar: (documento: DocumentoPadrao) => void;
  onExcluir: (documento: DocumentoPadrao) => void;
  onPreVisualizar: (documento: DocumentoPadrao) => void;
  onCarregarMaisUsos: (documento: DocumentoPadrao) => void;
  onBaixar: (documento: DocumentoPadrao) => void;
}
export function DocumentoPadraoDetalhesDrawer(t0) {
  const $ = _c(157);
  const {
    aberto,
    documento,
    setores,
    referencia,
    diasAviso,
    onFechar,
    onEditar,
    onSubstituirArquivo,
    onRestaurarVersao,
    onArquivar,
    onExcluir,
    onPreVisualizar,
    onCarregarMaisUsos,
    onBaixar
  } = t0;
  const [todosOsUsosVisiveis, setTodosOsUsosVisiveis] = useState(false);
  let t1;
  let t2;
  if ($[0] !== aberto) {
    t1 = () => {
      if (!aberto) {
        setTodosOsUsosVisiveis(false);
      }
    };
    t2 = [aberto];
    $[0] = aberto;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  if (!documento) {
    return null;
  }
  let t3;
  if ($[3] !== diasAviso || $[4] !== documento.validoAte || $[5] !== referencia) {
    t3 = situacaoValidade(documento.validoAte, referencia, diasAviso);
    $[3] = diasAviso;
    $[4] = documento.validoAte;
    $[5] = referencia;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const situacao = t3;
  let t4;
  if ($[7] !== documento.versaoAtual || $[8] !== documento.versoes) {
    let t5;
    if ($[10] !== documento.versaoAtual) {
      t5 = versao => versao.versao === documento.versaoAtual;
      $[10] = documento.versaoAtual;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    t4 = documento.versoes.find(t5);
    $[7] = documento.versaoAtual;
    $[8] = documento.versoes;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const versaoEnviada = t4;
  let t5;
  if ($[12] !== documento.usos || $[13] !== todosOsUsosVisiveis) {
    t5 = todosOsUsosVisiveis ? documento.usos : documento.usos.slice(0, USOS_VISIVEIS_INICIALMENTE);
    $[12] = documento.usos;
    $[13] = todosOsUsosVisiveis;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const usosVisiveis = t5;
  const usosRestantes = documento.totalUsos - usosVisiveis.length;
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="bg-brand-50 text-brand-700 flex h-10 w-10 flex-none items-center justify-center rounded-full"><FeatherFileText className="h-5 w-5" /></div>;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== documento.nome) {
    t7 = <span className="text-heading-3 font-heading-3 text-default-font truncate">{documento.nome}</span>;
    $[16] = documento.nome;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const t8 = DOCUMENTO_PADRAO_TIPO_LABEL[documento.tipo];
  let t9;
  if ($[18] !== t8) {
    t9 = <Badge variant="brand">{t8}</Badge>;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== situacao) {
    t10 = situacao === "SEM_VALIDADE" && <Badge variant="neutral">Não expira</Badge>;
    $[20] = situacao;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== documento.validoAte || $[23] !== situacao) {
    t11 = situacao === "VIGENTE" && <Badge variant="success">{`Válido até ${formatDataPrazo(documento.validoAte)}`}</Badge>;
    $[22] = documento.validoAte;
    $[23] = situacao;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== documento.validoAte || $[26] !== situacao) {
    t12 = situacao === "VENCENDO" && <Badge variant="warning">{`Vence em ${formatDataPrazo(documento.validoAte)}`}</Badge>;
    $[25] = documento.validoAte;
    $[26] = situacao;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== documento.validoAte || $[29] !== situacao) {
    t13 = situacao === "VENCIDA" && <Badge variant="error">{`Vencido em ${formatDataPrazo(documento.validoAte)}`}</Badge>;
    $[28] = documento.validoAte;
    $[29] = situacao;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const t14 = `v${documento.versaoAtual}`;
  let t15;
  if ($[31] !== t14) {
    t15 = <Badge variant="neutral">{t14}</Badge>;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== documento.arquivado) {
    t16 = documento.arquivado && <Badge variant="neutral">Arquivado</Badge>;
    $[33] = documento.arquivado;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t10 || $[36] !== t11 || $[37] !== t12 || $[38] !== t13 || $[39] !== t15 || $[40] !== t16 || $[41] !== t9) {
    t17 = <div className="flex flex-wrap items-center gap-2">{t9}{t10}{t11}{t12}{t13}{t15}{t16}</div>;
    $[35] = t10;
    $[36] = t11;
    $[37] = t12;
    $[38] = t13;
    $[39] = t15;
    $[40] = t16;
    $[41] = t9;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t17 || $[44] !== t7) {
    t18 = <div className="flex min-w-0 items-center gap-3">{t6}<div className="flex min-w-0 flex-col gap-1">{t7}{t17}</div></div>;
    $[43] = t17;
    $[44] = t7;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <FeatherX />;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== onFechar) {
    t20 = <IconButton variant="neutral-tertiary" icon={t19} onClick={onFechar} />;
    $[47] = onFechar;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== t18 || $[50] !== t20) {
    t21 = <div className="flex flex-none items-start justify-between gap-4 border-b border-solid border-neutral-100 px-6 py-4">{t18}{t20}</div>;
    $[49] = t18;
    $[50] = t20;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="text-brand-700 flex h-11 w-11 flex-none items-center justify-center rounded-[8px] bg-white"><FeatherFileText className="h-5 w-5" /></div>;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== documento.arquivo.nomeArquivo) {
    t23 = <p className="text-body-bold font-body-bold text-default-font truncate">{documento.arquivo.nomeArquivo}</p>;
    $[53] = documento.arquivo.nomeArquivo;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== documento.arquivo.tamanhoBytes) {
    t24 = formatFileSize(documento.arquivo.tamanhoBytes);
    $[55] = documento.arquivo.tamanhoBytes;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  const t25 = versaoEnviada?.enviadoEm ?? documento.criadoEm;
  let t26;
  if ($[57] !== t25) {
    t26 = formatDateEuropean(t25);
    $[57] = t25;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  const t27 = `${t24} · enviado por ${versaoEnviada?.enviadoPor ?? documento.criadoPor} em ${t26}`;
  let t28;
  if ($[59] !== t27) {
    t28 = <p className="text-caption font-caption text-subtext-color">{t27}</p>;
    $[59] = t27;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== t23 || $[62] !== t28) {
    t29 = <div className="min-w-0 flex-1">{t23}{t28}</div>;
    $[61] = t23;
    $[62] = t28;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <FeatherEye />;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  if ($[65] !== documento || $[66] !== onPreVisualizar) {
    t31 = <Button variant="neutral-secondary" size="small" icon={t30} onClick={() => onPreVisualizar(documento)}>Pré-visualizar</Button>;
    $[65] = documento;
    $[66] = onPreVisualizar;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  let t32;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <FeatherDownload />;
    $[68] = t32;
  } else {
    t32 = $[68];
  }
  let t33;
  if ($[69] !== documento || $[70] !== onBaixar) {
    t33 = <Button variant="neutral-secondary" size="small" icon={t32} onClick={() => onBaixar(documento)}>Baixar</Button>;
    $[69] = documento;
    $[70] = onBaixar;
    $[71] = t33;
  } else {
    t33 = $[71];
  }
  let t34;
  if ($[72] !== t29 || $[73] !== t31 || $[74] !== t33) {
    t34 = <div className="flex items-center gap-3 rounded-[12px] border border-solid border-neutral-200 bg-neutral-50 p-3">{t22}{t29}{t31}{t33}</div>;
    $[72] = t29;
    $[73] = t31;
    $[74] = t33;
    $[75] = t34;
  } else {
    t34 = $[75];
  }
  let t35;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <span className="text-caption-bold font-caption-bold text-subtext-color">Vinculado a</span>;
    $[76] = t35;
  } else {
    t35 = $[76];
  }
  const t36 = documento.parteVinculada ? `${documento.parteVinculada.nome} · ${documento.parteVinculada.cpfCnpj}` : "Sem v\xEDnculo \u2014 vale para qualquer parte";
  let t37;
  if ($[77] !== t36) {
    t37 = <div className="flex flex-col gap-0.5">{t35}<span className="text-body font-body text-default-font">{t36}</span></div>;
    $[77] = t36;
    $[78] = t37;
  } else {
    t37 = $[78];
  }
  let t38;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <span className="text-caption-bold font-caption-bold text-subtext-color">Quem pode usar</span>;
    $[79] = t38;
  } else {
    t38 = $[79];
  }
  let t39;
  if ($[80] !== documento.escopo || $[81] !== documento.setoresIds || $[82] !== setores) {
    t39 = rotuloEscopo(documento.escopo, documento.setoresIds, setores);
    $[80] = documento.escopo;
    $[81] = documento.setoresIds;
    $[82] = setores;
    $[83] = t39;
  } else {
    t39 = $[83];
  }
  let t40;
  if ($[84] !== t39) {
    t40 = <div className="flex flex-col gap-0.5">{t38}<span className="text-body font-body text-default-font">{t39}</span></div>;
    $[84] = t39;
    $[85] = t40;
  } else {
    t40 = $[85];
  }
  let t41;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <span className="text-caption-bold font-caption-bold text-subtext-color">Validade</span>;
    $[86] = t41;
  } else {
    t41 = $[86];
  }
  let t42;
  if ($[87] !== documento.validoAte) {
    t42 = documento.validoAte ? `Válido até ${formatDataPrazo(documento.validoAte)}` : "N\xE3o expira";
    $[87] = documento.validoAte;
    $[88] = t42;
  } else {
    t42 = $[88];
  }
  let t43;
  if ($[89] !== t42) {
    t43 = <div className="flex flex-col gap-0.5">{t41}<span className="text-body font-body text-default-font">{t42}</span></div>;
    $[89] = t42;
    $[90] = t43;
  } else {
    t43 = $[90];
  }
  let t44;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <span className="text-caption-bold font-caption-bold text-subtext-color">Produtos</span>;
    $[91] = t44;
  } else {
    t44 = $[91];
  }
  let t45;
  if ($[92] !== documento.produtos) {
    t45 = rotuloProdutos(documento.produtos);
    $[92] = documento.produtos;
    $[93] = t45;
  } else {
    t45 = $[93];
  }
  let t46;
  if ($[94] !== t45) {
    t46 = <div className="flex flex-col gap-0.5">{t44}<span className="text-body font-body text-default-font">{t45}</span></div>;
    $[94] = t45;
    $[95] = t46;
  } else {
    t46 = $[95];
  }
  let t47;
  if ($[96] !== t37 || $[97] !== t40 || $[98] !== t43 || $[99] !== t46) {
    t47 = <div className="mobile:grid-cols-1 grid grid-cols-2 gap-4 border-t border-solid border-neutral-100 pt-5">{t37}{t40}{t43}{t46}</div>;
    $[96] = t37;
    $[97] = t40;
    $[98] = t43;
    $[99] = t46;
    $[100] = t47;
  } else {
    t47 = $[100];
  }
  let t48;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <span className="text-body-bold font-body-bold text-default-font">Onde está sendo usado</span>;
    $[101] = t48;
  } else {
    t48 = $[101];
  }
  const t49 = `${documento.totalUsos} ${documento.totalUsos === 1 ? "uso" : "usos"}`;
  let t50;
  if ($[102] !== t49) {
    t50 = <div className="flex items-center justify-between">{t48}<Badge variant="neutral">{t49}</Badge></div>;
    $[102] = t49;
    $[103] = t50;
  } else {
    t50 = $[103];
  }
  let t51;
  if ($[104] !== documento || $[105] !== onCarregarMaisUsos || $[106] !== referencia || $[107] !== usosRestantes || $[108] !== usosVisiveis) {
    t51 = documento.totalUsos === 0 ? <span className="text-body font-body text-subtext-color">Este documento ainda não foi usado em nenhum processo.</span> : <div className="flex flex-col gap-2">{usosVisiveis.map(uso => <div key={uso.id} className="flex items-center gap-3 rounded-[12px] border border-solid border-neutral-200 p-3"><span className="text-brand-700 flex flex-none items-center">{iconeOrigem(uso.origem)}</span><div className="min-w-0 flex-1"><p className="text-body font-body text-default-font truncate">{`${uso.referencia} · ${uso.titulo}`}</p><p className="text-caption font-caption text-subtext-color">{rotuloUso(uso, referencia)}</p></div>{uso.statusRotulo ? <span className="text-caption font-caption text-subtext-color flex-none">{uso.statusRotulo}</span> : uso.href && <Link href={uso.href} className="text-caption-bold font-caption-bold text-brand-700 flex-none">Abrir</Link>}</div>)}{usosRestantes > 0 && <Button variant="brand-tertiary" size="small" className="w-fit" onClick={() => {
        setTodosOsUsosVisiveis(true);
        if (documento.totalUsos > documento.usos.length) {
          onCarregarMaisUsos(documento);
        }
      }}>{`Ver os outros ${usosRestantes} usos`}</Button>}</div>;
    $[104] = documento;
    $[105] = onCarregarMaisUsos;
    $[106] = referencia;
    $[107] = usosRestantes;
    $[108] = usosVisiveis;
    $[109] = t51;
  } else {
    t51 = $[109];
  }
  let t52;
  if ($[110] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <div className="bg-brand-50 flex items-start gap-2 rounded-[8px] p-3"><FeatherInfo className="text-brand-700 mt-0.5 h-3.5 w-3.5 flex-none" /><span className="text-caption font-caption text-brand-800">Processos já enviados guardam a cópia do arquivo. Editar ou excluir aqui não altera nada que já foi protocolado.</span></div>;
    $[110] = t52;
  } else {
    t52 = $[110];
  }
  let t53;
  if ($[111] !== t50 || $[112] !== t51) {
    t53 = <div className="flex flex-col gap-3 border-t border-solid border-neutral-100 pt-5">{t50}{t51}{t52}</div>;
    $[111] = t50;
    $[112] = t51;
    $[113] = t53;
  } else {
    t53 = $[113];
  }
  let t54;
  if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = <span className="text-body-bold font-body-bold text-default-font">Histórico de versões</span>;
    $[114] = t54;
  } else {
    t54 = $[114];
  }
  let t55;
  if ($[115] !== documento || $[116] !== onRestaurarVersao) {
    t55 = documento.versoes.map(versao_0 => {
      const emUso = versao_0.versao === documento.versaoAtual;
      return <div key={versao_0.versao} className={`flex items-center gap-3 rounded-[12px] border border-solid p-3 ${emUso ? "border-brand-300 bg-brand-50" : "border-neutral-200"}`}><div className="min-w-0 flex-1"><p className="text-body font-body text-default-font truncate">{`v${versao_0.versao} · ${versao_0.nomeArquivo}`}</p><p className="text-caption font-caption text-subtext-color">{`${formatDateEuropean(versao_0.enviadoEm)} · ${versao_0.enviadoPor}`}</p></div>{emUso ? <Badge variant="brand">Em uso</Badge> : <Button variant="neutral-secondary" size="small" icon={<FeatherRotateCcw />} onClick={() => onRestaurarVersao(documento, versao_0.versao)}>Restaurar</Button>}</div>;
    });
    $[115] = documento;
    $[116] = onRestaurarVersao;
    $[117] = t55;
  } else {
    t55 = $[117];
  }
  let t56;
  if ($[118] !== t55) {
    t56 = <div className="flex flex-col gap-3 border-t border-solid border-neutral-100 pt-5">{t54}<div className="flex flex-col gap-2">{t55}</div></div>;
    $[118] = t55;
    $[119] = t56;
  } else {
    t56 = $[119];
  }
  let t57;
  if ($[120] !== t34 || $[121] !== t47 || $[122] !== t53 || $[123] !== t56) {
    t57 = <div className="flex grow flex-col gap-6 overflow-y-auto p-6">{t34}{t47}{t53}{t56}</div>;
    $[120] = t34;
    $[121] = t47;
    $[122] = t53;
    $[123] = t56;
    $[124] = t57;
  } else {
    t57 = $[124];
  }
  let t58;
  if ($[125] !== documento || $[126] !== onArquivar) {
    t58 = !documento.arquivado && <Button variant="neutral-tertiary" size="small" icon={<FeatherArchive />} onClick={() => onArquivar(documento)}>Arquivar</Button>;
    $[125] = documento;
    $[126] = onArquivar;
    $[127] = t58;
  } else {
    t58 = $[127];
  }
  let t59;
  if ($[128] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = <FeatherTrash2 />;
    $[128] = t59;
  } else {
    t59 = $[128];
  }
  let t60;
  if ($[129] !== documento || $[130] !== onExcluir) {
    t60 = <Button variant="destructive-tertiary" size="small" icon={t59} onClick={() => onExcluir(documento)}>Excluir</Button>;
    $[129] = documento;
    $[130] = onExcluir;
    $[131] = t60;
  } else {
    t60 = $[131];
  }
  let t61;
  if ($[132] !== t58 || $[133] !== t60) {
    t61 = <div className="flex items-center gap-2">{t58}{t60}</div>;
    $[132] = t58;
    $[133] = t60;
    $[134] = t61;
  } else {
    t61 = $[134];
  }
  let t62;
  if ($[135] === Symbol.for("react.memo_cache_sentinel")) {
    t62 = <FeatherUpload />;
    $[135] = t62;
  } else {
    t62 = $[135];
  }
  let t63;
  if ($[136] !== documento || $[137] !== onSubstituirArquivo) {
    t63 = <Button variant="neutral-secondary" icon={t62} onClick={() => onSubstituirArquivo(documento)}>Substituir arquivo</Button>;
    $[136] = documento;
    $[137] = onSubstituirArquivo;
    $[138] = t63;
  } else {
    t63 = $[138];
  }
  let t64;
  if ($[139] === Symbol.for("react.memo_cache_sentinel")) {
    t64 = <FeatherEdit3 />;
    $[139] = t64;
  } else {
    t64 = $[139];
  }
  let t65;
  if ($[140] !== documento || $[141] !== onEditar) {
    t65 = <Button icon={t64} onClick={() => onEditar(documento)}>Editar</Button>;
    $[140] = documento;
    $[141] = onEditar;
    $[142] = t65;
  } else {
    t65 = $[142];
  }
  let t66;
  if ($[143] !== t63 || $[144] !== t65) {
    t66 = <div className="flex items-center gap-3">{t63}{t65}</div>;
    $[143] = t63;
    $[144] = t65;
    $[145] = t66;
  } else {
    t66 = $[145];
  }
  let t67;
  if ($[146] !== t61 || $[147] !== t66) {
    t67 = <div className="mobile:flex-col mobile:items-stretch flex flex-none items-center justify-between gap-3 border-t border-solid border-neutral-100 bg-neutral-50 px-6 py-4">{t61}{t66}</div>;
    $[146] = t61;
    $[147] = t66;
    $[148] = t67;
  } else {
    t67 = $[148];
  }
  let t68;
  if ($[149] !== t21 || $[150] !== t57 || $[151] !== t67) {
    t68 = <Drawer.Content className="max-w-2xl"><div className="flex h-full w-full flex-col">{t21}{t57}{t67}</div></Drawer.Content>;
    $[149] = t21;
    $[150] = t57;
    $[151] = t67;
    $[152] = t68;
  } else {
    t68 = $[152];
  }
  let t69;
  if ($[153] !== aberto || $[154] !== onFechar || $[155] !== t68) {
    t69 = <Drawer open={aberto} onOpenChange={onFechar}>{t68}</Drawer>;
    $[153] = aberto;
    $[154] = onFechar;
    $[155] = t68;
    $[156] = t69;
  } else {
    t69 = $[156];
  }
  return t69;
}